<template>
  <div>
    <div class="card top20card" ref="top20card">
      <b-skeleton-wrapper :loading="isLoading">
        <template #loading>
          <div class="card-body">
            <b-skeleton width="85%" />
            <b-skeleton width="55%" />
            <b-skeleton width="70%" />
          </div>
        </template>
        <div class="card-body">
          <div class="row mb-4">
            <div class="col-md-8">
              <div class="d-flex flex-wrap">
                <div>
                  <h5 class="mb-1">{{ cardTitle }}</h5>
                  <p class="mb-0 font-size-10">
                    {{ momentDateShortMonth(period?.[0]) }} -
                    {{ momentDateShortMonth(period?.[1]) }}, {{ fleetCategory?.label }}
                  </p>
                </div>
              </div>
            </div>
            <div class="col-md-3">
              <b-button-group class="mb-3">
                <b-button @click="isTable = true" :variant="isTable ? 'primary' : 'outline-primary'"
                  ><i class="mc-fa icon-outline_browser"></i>
                  {{ $t('report.object.table') }}</b-button
                >
                <b-button
                  @click="isTable = false"
                  :variant="isTable ? 'outline-primary' : 'primary'"
                  ><i class="mc-fa icon-fill_bar-chart-2"></i>
                  {{ $t('report.object.chart') }}</b-button
                >
              </b-button-group>
            </div>
            <div class="col-md-1">
              <div class="float-end">
                <div class="mc-table-filter">
                  <div class="mc-table-view">
                    <b-dropdown dropleft variant="white">
                      <template v-slot:button-content>
                        <i class="mc-fa icon-outline_more-vertical"></i>
                      </template>
                      <b-dropdown-item @click="openDatepicker()">{{
                        $t('global.object.period')
                      }}</b-dropdown-item>
                      <b-dropdown-item @click="openModal()">
                        <i class="mc-fa icon-outline_settings"></i>
                        {{ $t('global.object.configuration') }}
                      </b-dropdown-item>
                      <mc-date-picker
                        v-show="isShow"
                        class="ms-2"
                        v-model="period"
                        :placeholder="$t('global.action.selectDate')"
                        format="DD MMM YYYY"
                        value-type="YYYY-MM-DD"
                        :shortcuts="makeShortcuts"
                        :draft="true"
                        :disabled="isLayoutEdit"
                        range-separator=" - "
                        range
                        :confirm="true"
                        :confirm-text="$t('global.action.apply')"
                        ref="top20PeriodPicker"
                        :popup-style="{
                          position: 'fixed',
                          top: '50%',
                          left: '0',
                          right: '0',
                          margin: '0 auto',
                          padding: '20px',
                          width: 'fit-content',
                          transform: 'translateY(-50%)',
                        }"
                        :disabled-date="disabledRange"
                        @pick="pickDate"
                        @input="applyFilter"
                        :lang="datePickerLang"
                      >
                        <template #header>
                          <h5 class="text-center mc-general__label">
                            {{ `${$t('global.object.period')} ${cardTitle}` }}
                          </h5>
                        </template>
                        <template #footer>
                          <b-button
                            class="mx-2"
                            variant="outline-primary"
                            @click="closeCalendar()"
                            >{{ $t('global.action.cancel') }}</b-button
                          >
                        </template>
                      </mc-date-picker>
                    </b-dropdown>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div v-if="isTable">
            <div class="table-responsive my-3 border border-1 mc-table-overflow">
              <b-table :items="items" :fields="computedFields" fixed show-empty>
                <template #cell(num)="row">
                  <p>{{ row.index + 1 }}</p>
                </template>
              </b-table>
            </div>
          </div>
          <div v-if="!isTable">
            <HorizontalBarChart
              :labels="chartData.labels"
              :datasets="chartData.datasets"
              :xLabel="ids == 'bestUsage' ? $t('dashboard.object.liter') : 'Km/L'"
              barLabel
            />
          </div>
          <p v-if="ids == 'bestUsage'" class="text-center">{{ $t('dashboard.chart.fuelUsage') }}</p>
          <p v-else-if="ids == 'worstUsage'" class="text-center">
            {{ $t('dashboard.text.standard') }}
            <span class="font-weight-bold">{{ fleetCategory?.label }}</span>
            {{ $t('dashboard.text.is') }}
            <span class="font-weight-bold">{{ fleetCategory?.fuelEfficiency }}</span> Km/L
          </p>
        </div>
      </b-skeleton-wrapper>
    </div>
    <b-modal :id="ids" :title="$t('dashboard.title.fuelUsageConfiguration')" size="md" centered>
      <b-form-group
        id="fleet-category"
        :label="$t('global.field.vehicleType')"
        label-for="category-option"
      >
        <multiselect
          id="category-option"
          v-model="fleetCategory"
          :options="categoryOptions"
          track-by="vehicleTypeId"
          label="label"
          :placeholder="$t('dashboard.label.chooseFleetType')"
        />
      </b-form-group>
      <template #modal-footer="{ cancel }">
        <div class="w-100">
          <a class="text-primary float-left" @click="resetFilter">{{
            $t('global.action.reset')
          }}</a>
          <div class="d-flex float-end">
            <McButton
              variant="secondary"
              class="me-2"
              :text="$t('global.action.cancel')"
              @click="cancel()"
            />
            <McButton variant="primary" :text="$t('global.action.apply')" @click="applyFilter" />
          </div>
        </div>
      </template>
    </b-modal>
  </div>
</template>
<script>
import { Helper } from '@/mixin/helper'
import moment from 'moment'
import HorizontalBarChart from '@/components/Chart/HorizontalBar'
import Multiselect from 'vue-multiselect'
import McButton from '@/components/McButton'
import { GET_VEHICLE_TYPE } from '@/services/api/setting/fleet/fleetType'
import datepickerShortcuts from '../lib/constants/date-picker-shortcuts'
import datePickerLang from '@/helpers/date-picker-lang'
import McDatePicker from '@/components/McDatePicker'

moment.locale('id')

export default {
  components: { HorizontalBarChart, Multiselect, McButton, McDatePicker },
  mixins: [Helper],
  apollo: {
    vehicleTypes: {
      fetchPolicy: 'no-cache',
      query: GET_VEHICLE_TYPE,
      variables() {
        return {
          filter: {
            hasFuelSensor: true,
          },
        }
      },
      result({ data, loading }) {
        if (!loading) {
          this.categoryOptions = data.vehicleTypes.vehicleTypes.data.map((item) => {
            return {
              vehicleTypeId: item.vehicleTypeId,
              label: `${item.model}/${item.type}`,
              fuelEfficiency: item.fuelEfficiency,
            }
          })
          this.fleetCategory = this.categoryOptions[0]
        }
      },
    },
  },
  props: {
    isLayoutEdit: { type: Boolean, default: false },
    cardTitle: { type: String, required: true },
    fields: { required: true },
    ids: {
      type: String,
      required: false,
    },
  },
  data() {
    return {
      isLoading: true,
      isTable: false,
      period: [moment().subtract(6, 'd').format('YYYY-MM-DD'), moment().format('YYYY-MM-DD')],
      isShow: false,
      items: [],
      chartData: {
        labels: [],
        datasets: [],
      },
      chartOptions: {
        indexAxis: 'y',
      },
      fleetCategory: {},
      categoryOptions: [],
      pastDate: moment().subtract(1, 'years'),
      futureEnd: moment().add(1, 'years'),
      vehicleId: null,
    }
  },
  computed: {
    makeShortcuts() {
      return datepickerShortcuts(this, `${this.ids}-config`, {
        vehicleTypeId: [this.vehicleId],
      })
    },
    computedFields() {
      return [
        {
          key: 'num',
          label: 'No.',
        },
        ...this.fields,
      ]
    },
    datePickerLang() {
      return datePickerLang()
    },
  },
  methods: {
    openDatepicker() {
      this.$refs.top20PeriodPicker.openPopup()
    },
    closeCalendar() {
      this.$refs.top20PeriodPicker.cancel()
    },
    openModal() {
      this.$bvModal.show(this.ids)
    },
    disabledRange: function (date) {
      return date <= this.pastDate || date >= this.futureEnd || date > new Date()
    },
    pickDate(date) {
      let maxDate = 30
      this.pastDate = new Date(date.getTime() - 24 * maxDate * 3600 * 1000)

      // Get the current date and time
      let currentDate = new Date()

      if (new Date(date.getTime() + 24 * 0 * 3600 * 1000).getTime() === currentDate.getTime()) {
        this.futureEnd = new Date(date.getTime() + 24 * 0 * 3600 * 1000)
      } else {
        this.futureEnd = new Date(date.getTime() + 24 * maxDate * 3600 * 1000)
      }
    },
    initData(data) {
      this.items = data
      this.chartData.labels = data.map((item) => item.licensePlate)
      setTimeout(() => {
        this.isLoading = false
      }, 800)
    },
    async initLocalStorage(configName, vgroupId) {
      await this.$apollo.queries.vehicleTypes.refetch({
        filter: {
          hasFuelSensor: true,
          vgroupId,
        },
      })
      localStorage.setItem(
        configName,
        JSON.stringify({
          startDate: moment(this.period[0] + ' 00:00:00.000').toISOString(),
          endDate: moment(this.period[1] + ' 23:59:59.000').toISOString(),
          vehicleTypeId: [this.fleetCategory?.vehicleTypeId],
          isShortcut: false,
          index: 0,
        })
      )
    },
    async loadLocalStorage(data, vgroupId) {
      await this.$apollo.queries.vehicleTypes.refetch({
        filter: {
          hasFuelSensor: true,
          vgroupId,
        },
      })
      if (data.isShortcut) {
        const generateDate = this.makeShortcuts[data.index].onClick()
        this.period[0] = this.momentYmD(generateDate[0])
        this.period[1] = this.momentYmD(generateDate[1])
      } else {
        this.period[0] = this.momentYmD(data.startDate)
        this.period[1] = this.momentYmD(data.endDate)
      }
      this.vehicleId = data.vehicleTypeId
      const vehicleType = this.categoryOptions.filter((item) =>
        data.vehicleTypeId.includes(item.vehicleTypeId)
      )
      this.fleetCategory = vehicleType[0]
    },
    applyFilter() {
      const config = JSON.parse(localStorage.getItem(`${this.ids}-config`))
      const filter = {
        startDate: moment(this.period[0] + ' 00:00:00.000').toISOString(),
        endDate: moment(this.period[1] + ' 23:59:59.000').toISOString(),
        vehicleTypeId: [this.fleetCategory?.vehicleTypeId],
        isShortcut: false,
        index: 0,
      }
      if (!config.isShortcut) {
        localStorage.setItem(`${this.ids}-config`, JSON.stringify(filter))
      }
      this.$emit('filter', filter)
      this.$bvModal.hide(this.ids)
    },
    resetFilter() {
      this.fleetCategory = this.categoryOptions[0]
      this.applyFilter()
    },
  },
}
</script>
<style lang="scss">
.top20card {
  min-height: 548px;
}
</style>
